const WEEKDAY_IDS = { 6: 1, 0: 2, 1: 3, 2: 4, 3: 5, 4: 6, 5: 7 };
const WEEKDAY_NAMES = {
  1: ['sat', 'saturday'], 2: ['sun', 'sunday'], 3: ['mon', 'monday'],
  4: ['tue', 'tues', 'tuesday'], 5: ['wed', 'wednesday'],
  6: ['thu', 'thur', 'thurs', 'thursday'], 7: ['fri', 'friday']
};

function parseDate(value) {
  const [year, month, day] = String(value || '').split('-').map(Number);
  return year && month && day ? new Date(year, month - 1, day) : null;
}

function dateKey(date) {
  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')].join('-');
}

// Match the attendance policy: tutors have the whole session day to mark it.
export function normalizeReportSessionState(session, today) {
  if (!today) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Africa/Nairobi', year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    today = `${values.year}-${values.month}-${values.day}`;
  }
  const state = String(session.state || session.status || '').toLowerCase();
  const sessionDate = String(session.session_date || session.date || '').slice(0, 10);
  if (state === 'pending' && /^\d{4}-\d{2}-\d{2}$/.test(sessionDate) && sessionDate < today) {
    return { ...session, state: 'missed', status: 'missed' };
  }
  return session;
}

function scheduledOn(subject, periodIndex, weekdayId) {
  const schedule = subject.schedule || {};
  const ids = schedule[`period_${periodIndex + 1}_ids`];
  if (Array.isArray(ids) && ids.length) return ids.map(Number).includes(weekdayId);
  const names = schedule[`period_${periodIndex + 1}`];
  return Array.isArray(names) && names.some((name) =>
    WEEKDAY_NAMES[weekdayId].includes(String(name).trim().toLowerCase()));
}

export function calculateTimetableSessions(timetable) {
  const periods = timetable.periods || [];
  const exceptions = (timetable.exception_dates || []).map((item) => ({
    ...item, start: parseDate(item.start_date), end: parseDate(item.end_date)
  })).filter((item) => item.start && item.end);
  const midterm = exceptions.find((item) => item.is_midterm) ||
    exceptions.find((item) => /mid\s*-?\s*term/i.test(`${item.name || ''} ${item.reason || ''}`));
  const start = parseDate(timetable.start_date);
  const end = parseDate(timetable.end_date);

  return (timetable.subjects || []).map((subject) => {
    const slots = [];
    const beforeWeeks = new Set();
    const afterWeeks = new Set();
    let beforeSessions = 0;
    let afterSessions = 0;
    const weeklySessions = Object.keys(WEEKDAY_NAMES).reduce((sum, day) =>
      sum + periods.reduce((count, _, index) => count + Number(scheduledOn(subject, index, Number(day))), 0), 0);

    if (start && end) {
      for (const cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
        if (exceptions.some((item) => cursor >= item.start && cursor <= item.end)) continue;
        const weekdayId = WEEKDAY_IDS[cursor.getDay()];
        const weekStart = new Date(cursor);
        weekStart.setDate(cursor.getDate() - ((cursor.getDay() + 1) % 7));
        const weekKey = dateKey(weekStart);
        for (let index = 0; index < periods.length; index += 1) {
          if (!scheduledOn(subject, index, weekdayId)) continue;
          const period = periods[index];
          slots.push({
            timetable_id: timetable.id,
            subject_id: subject.subject_id,
            subject: subject.subject_name,
            session_date: dateKey(cursor),
            period: period.name || `Period ${index + 1}`,
            period_id: period.id,
            time_start: period.start_time,
            time_end: period.end_time,
            campus: timetable.campus,
            state: 'pending'
          });
          if (midterm?.start && cursor < midterm.start) {
            beforeSessions += 1;
            beforeWeeks.add(weekKey);
          } else if (midterm?.end && cursor > midterm.end) {
            afterSessions += 1;
            afterWeeks.add(weekKey);
          } else if (!midterm) {
            beforeSessions += 1;
            beforeWeeks.add(weekKey);
          }
        }
      }
    }

    return {
      id: subject.id,
      subjectId: subject.subject_id,
      name: subject.subject_name || subject.name || 'Subject',
      weeklySessions,
      beforeWeeks: beforeWeeks.size,
      beforeSessions,
      afterWeeks: afterWeeks.size,
      afterSessions,
      teachingWeeks: new Set([...beforeWeeks, ...afterWeeks]).size,
      totalSessions: slots.length,
      slots
    };
  });
}
